import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import {
  FormField,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../utility/errorSanitization";
import {
  DEFAULT_TOURNAMENT_LANE_SETTINGS,
  DOUBLES_SPLIT,
  DOUBLES_TOGETHER,
  MOVEMENT_LEFT,
  MOVEMENT_RIGHT,
  MOVEMENT_STAY,
  doublesPlacementSummary,
  emptyTournamentLaneSettings,
  movementSummary,
  resolveTournamentMovementRules,
} from "../../utility/laneAssignment";
import {
  fetchTournamentLaneSettings,
  saveTournamentLaneSettings,
} from "../../utility/laneAssignmentApi";

const DetailRow = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium text-brand-muted mb-1">{label}</div>
    <div className="text-sm text-brand-charcoal">{value}</div>
  </div>
);

const LaneAssignmentSetupCard = ({ tournamentId }) => {
  const [saved, setSaved] = useState(emptyTournamentLaneSettings());
  const [fields, setFields] = useState(emptyTournamentLaneSettings());
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tournamentId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const next = await fetchTournamentLaneSettings(tournamentId);
        if (cancelled) return;
        setSaved(next);
        setFields(next);
      } catch (error) {
        if (!cancelled) {
          notify.error(sanitizeOperationError.read(error, "lane assignment"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  const handleStartEdit = () => {
    setFields(saved);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(saved);
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const next = {
      ...DEFAULT_TOURNAMENT_LANE_SETTINGS,
      ...fields,
      encoding: saved.encoding,
      movePairs: Math.max(1, Number(fields.movePairs) || 1),
      movementRules: resolveTournamentMovementRules(saved, fields),
    };
    setSaving(true);
    try {
      const stored = await saveTournamentLaneSettings(tournamentId, next);
      setSaved(stored);
      setFields(stored);
      setIsEditing(false);
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "lane assignment"));
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const display = isEditing ? fields : saved;

  if (loading) {
    return (
      <div className="card-board p-6">
        <h2 className="heading-display text-xl mb-6">Lane assignment</h2>
        <div className="card-inset p-4 flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="card-board p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="heading-display text-xl">Lane assignment</h2>
        </div>
        {!isEditing ? (
          <SecondaryButton
            type="button"
            onClick={handleStartEdit}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm flex-shrink-0"
          >
            Edit
          </SecondaryButton>
        ) : null}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="card-inset p-4 space-y-6">
          <FormField
            label="Movement"
            type="select"
            value={fields.movement}
            onChange={(e) => setField("movement", e.target.value)}
          >
            <option value={MOVEMENT_STAY}>Stay on the same pair</option>
            <option value={MOVEMENT_RIGHT}>Move pairs to the right</option>
            <option value={MOVEMENT_LEFT}>Move pairs to the left</option>
          </FormField>

          {fields.movement !== MOVEMENT_STAY ? (
            <FormField
              label="How many pairs"
              type="number"
              min="1"
              max="20"
              value={fields.movePairs}
              onChange={(e) => setField("movePairs", e.target.value)}
            />
          ) : null}

          <FormField
            label="Doubles on a pair"
            type="select"
            value={fields.doublesPlacement}
            onChange={(e) => setField("doublesPlacement", e.target.value)}
          >
            <option value={DOUBLES_TOGETHER}>Whole team on one lane</option>
            <option value={DOUBLES_SPLIT}>Split across the pair</option>
          </FormField>

          <div className="flex flex-wrap gap-2">
            <PrimaryButton type="submit" loading={saving}>
              Save
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </SecondaryButton>
          </div>
        </form>
      ) : (
        <div className="card-inset p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <DetailRow label="Movement" value={movementSummary(display)} />
          <DetailRow
            label="Doubles on a pair"
            value={doublesPlacementSummary(display)}
          />
        </div>
      )}
    </div>
  );
};

export default LaneAssignmentSetupCard;
